import { AppointmentService } from '../../api/services/AppointmentService';
import type { SlotRescheduleCandidateDto } from './slotRescheduleCandidateTypes';
import type { 
  Routemize_DTOs_AppointmentDTO,
  Routemize_DTOs_AppointmentListItem,
  Routemize_DTOs_AppointmentTimelineResponse,
  Routemize_DTOs_AppointmentDetailDTO,
  Routemize_DTOs_CreateAppointmentDTO,
  Routemize_DTOs_UpdateAppointmentDTO,
  Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1,
  Routemize_Core_Dtos_PagedResultDto_1
} from '../../api/models';
import { Routemize_Constants_AppointmentStatus } from '../../api/models/Routemize_Constants_AppointmentStatus';
import { Routemize_Constants_DateFilterType } from '../../api/models/Routemize_Constants_DateFilterType';

/**
 * Date filter presets for appointments - matches API enum
 */
export type DateFilterPreset = 'today' | 'last-7-days' | 'this-week' | 'last-30-days' | 'this-month' | 'this-year' | 'all';

/**
 * Search and filter parameters for appointments
 */
export interface AppointmentSearchParams {
  query?: string;
  status?: Routemize_Constants_AppointmentStatus[];
  teamMemberId?: string;
  dateFilter?: DateFilterPreset;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
}

/**
 * AppointmentManager - Business logic for appointment management  
 * Handles the orchestration between UI requirements and API contracts
 * Follows DDD pattern: UI → Manager → API
 * Works directly with API DTOs - no custom DTOs
 */
export class AppointmentManager {
  private readonly timezone?: string;

  constructor(timezone?: string) {
    this.timezone = timezone;
  }

  /**
   * Get appointments for timeline view with route optimization data.
   *
   * Routing distances are computed by the backend via Google Distance Matrix. To
   * avoid the geocoder mis-resolving ambiguous address strings, every input is
   * passed as lat/lng. Always send `startingCoords` / `lunchCoords` when you
   * have them — the address string is display/fallback only.
   *
   * @param date - Target date for timeline
   * @param teamMemberId - Optional team member filter
   * @param startingLocation - Tag for the starting point UI (home/office) — informational only
   * @param startingAddress - Human-readable address for display (NOT used for routing when coords are present)
   * @param startingCoords - Lat/lng of the starting point (REQUIRED for accurate routing)
   * @param lunchAddress - Human-readable lunch address for display (NOT used for routing when coords are present)
   * @param lunchCoords - Lat/lng of the lunch break location (REQUIRED for accurate routing if lunch enabled)
   * @returns Timeline response from API
   */
  async getTimelineData(
    date: Date,
    teamMemberId?: string,
    startingLocation: 'home' | 'office' = 'home',
    startingAddress?: string,
    startingCoords?: { lat: number; lng: number } | null,
    lunchAddress?: string,
    lunchCoords?: { lat: number; lng: number } | null,
    lunchStartTime?: string,
    lunchEndTime?: string
  ): Promise<Routemize_DTOs_AppointmentTimelineResponse> {
    const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : undefined;

    const response = await AppointmentService.getApiAppAppointmentGetUserTimeline(
      teamMemberId,
      startingAddress,
      startingCoords?.lat,
      startingCoords?.lng,
      dateStr,
      lunchAddress || undefined,
      lunchCoords?.lat,
      lunchCoords?.lng,
      lunchStartTime || undefined,
      lunchEndTime || undefined
    );

    // Drop appointment entries whose contact has been deleted so they don't
    // appear on the timeline. Timeline items without an attached appointment
    // (gaps, travel, etc.) pass through untouched.
    const items = (response.items ?? []).filter((item) => {
      if (!item.appointment) return true;
      const name = item.appointment.contactName?.trim();
      return !!name;
    });

    return { ...response, items };
  }

  /**
   * Candidates for “move into this gap” suggestions (future scheduled + cancelled, with coordinates).
   * Returns [] if the endpoint is not deployed yet (404/501) or on failure.
   *
   * Smart Fill must never propose appointments that don't belong to the user
   * whose timeline is being viewed — otherwise a user could pull another
   * teammate's appointment into their own slot. We pass `userId` to the
   * backend filter AND defensively re-check it on the client so a backend
   * regression can't widen the scope.
   */
  async getSlotRescheduleCandidates(
    userId: string,
    fromUtc: Date,
    toUtc: Date
  ): Promise<SlotRescheduleCandidateDto[]> {
    const trimmedUserId = (userId ?? '').trim();
    if (!trimmedUserId) {
      console.warn('getSlotRescheduleCandidates: refusing to query without a userId');
      return [];
    }

    try {
      const searchResponse = await AppointmentService.postApiAppAppointmentSearch({
        maxResultCount: 300,
        skipCount: 0,
        sorting: 'meetingStartTimeUtc asc',
        filter: {
          userId: trimmedUserId,
          startDate: fromUtc.toISOString(),
          endDate: toUtc.toISOString(),
          includeExternalEvents: false,
        },
      } as Parameters<typeof AppointmentService.postApiAppAppointmentSearch>[0]);

      // Defense in depth: even though the backend filter is sent above,
      // verify the response only contains appointments where this user is an
      // attendee. Drop anything we can't positively attribute to them.
      const belongsToUser = (item: Routemize_DTOs_AppointmentListItem): boolean => {
        const attendees = item.attendees ?? [];
        if (attendees.length === 0) return false;
        return attendees.some((a) => (a?.id ?? '').trim() === trimmedUserId);
      };

      const listItems =
        ((searchResponse as { items?: Routemize_DTOs_AppointmentListItem[] })?.items ?? [])
          .filter((item): item is Routemize_DTOs_AppointmentListItem & { id: string } => !!item.id)
          .filter(belongsToUser);

      const isComplete = (item: Routemize_DTOs_AppointmentListItem): boolean => {
        if (item.latitude == null || item.longitude == null) return false;
        if (item.latitude === 0 && item.longitude === 0) return false;
        return true;
      };

      const scheduledValues = new Set<string | number>([
        Routemize_Constants_AppointmentStatus.SCHEDULED,
        'Scheduled', 'scheduled',
        0, 1,
      ]);
      const cancelledValues = new Set<string | number>([
        Routemize_Constants_AppointmentStatus.CANCELLED,
        'Cancelled', 'cancelled',
        3,
      ]);
      const isScheduled = (status: unknown): boolean =>
        scheduledValues.has(status as string | number) || scheduledValues.has(String(status));
      const isCancelled = (status: unknown): boolean =>
        cancelledValues.has(status as string | number) || cancelledValues.has(String(status));

      const nowMs = Date.now();
      const scheduledPool = listItems
        .filter((item) => {
          if (!isScheduled(item.status)) return false;
          const start = item.meetingStartTimeUtc ? new Date(item.meetingStartTimeUtc) : null;
          return !!start && !Number.isNaN(start.getTime()) && start.getTime() > nowMs && isComplete(item);
        })
        .sort((a, b) => {
          const aMs = a.meetingStartTimeUtc ? new Date(a.meetingStartTimeUtc).getTime() : Number.POSITIVE_INFINITY;
          const bMs = b.meetingStartTimeUtc ? new Date(b.meetingStartTimeUtc).getTime() : Number.POSITIVE_INFINITY;
          return aMs - bMs;
        })
        .slice(0, 40);

      const cancelledPool = listItems
        .filter((item) => {
          if (!isCancelled(item.status)) return false;
          const start = item.meetingStartTimeUtc ? new Date(item.meetingStartTimeUtc) : null;
          return !!start && !Number.isNaN(start.getTime()) && isComplete(item);
        })
        .sort((a, b) => {
          const aMs = a.meetingStartTimeUtc ? new Date(a.meetingStartTimeUtc).getTime() : 0;
          const bMs = b.meetingStartTimeUtc ? new Date(b.meetingStartTimeUtc).getTime() : 0;
          return bMs - aMs;
        })
        .slice(0, 40);

      return [...scheduledPool, ...cancelledPool]
        .map((item) => {
          const contactName = `${item.contactFirstName ?? ''} ${item.contactLastName ?? ''}`.trim();
          const startDate = item.meetingStartTimeUtc ? new Date(item.meetingStartTimeUtc) : null;
          const endDate = item.meetingEndTimeUtc ? new Date(item.meetingEndTimeUtc) : null;
          const inferredDurationMinutes =
            startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate > startDate
              ? Math.max(Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60)), 1)
              : null;

          const normalizedStatus = isScheduled(item.status)
            ? Routemize_Constants_AppointmentStatus.SCHEDULED
            : Routemize_Constants_AppointmentStatus.CANCELLED;

          return {
            id: item.id,
            contactName: contactName || item.eventName || 'Customer',
            status: normalizedStatus,
            meetingStartTimeUtc: item.meetingStartTimeUtc ?? undefined,
            meetingEndTimeUtc: item.meetingEndTimeUtc ?? undefined,
            latitude: item.latitude!,
            longitude: item.longitude!,
            source: item.source ?? null,
            timeAtLocationMinutes: inferredDurationMinutes,
            preBufferMinutes: null,
            postBufferMinutes: null,
            preferredTimeLabel: item.preferredTimeLabel ?? null,
          } satisfies SlotRescheduleCandidateDto;
        });
    } catch (e) {
      return [];
    }
  }

  /**
   * Map our preset to API enum
   */
  private mapDateFilterToApiEnum(preset?: DateFilterPreset): Routemize_Constants_DateFilterType {
    if (!preset || preset === 'all') return Routemize_Constants_DateFilterType.NONE;
    
    switch (preset) {
      case 'today':
        return Routemize_Constants_DateFilterType.LAST24HOURS;
      case 'last-7-days':
        return Routemize_Constants_DateFilterType.LAST7DAYS;
      case 'this-week':
        return Routemize_Constants_DateFilterType.THIS_WEEK;
      case 'last-30-days':
        return Routemize_Constants_DateFilterType.LAST30DAYS;
      case 'this-month':
        return Routemize_Constants_DateFilterType.THIS_MONTH;
      case 'this-year':
        return Routemize_Constants_DateFilterType.THIS_YEAR;
      default:
        return Routemize_Constants_DateFilterType.NONE;
    }
  }

  /**
   * Search appointments with filtering and pagination
   * @param params - Search and filter parameters
   * @returns Paginated appointment results from API
   *
   * Results are post-processed to drop "orphaned" appointments — records
   * whose linked contact has been deleted (no contactId and no contact name
   * left on the appointment). Those rows should not surface in user-facing
   * lists such as the dashboard or bookings timeline.
   */
  async searchAppointments(params: AppointmentSearchParams): Promise<Routemize_Core_Dtos_PagedResultDto_1> {
    const searchFilter: Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1 = {
      skipCount: params.skipCount || 0,
      maxResultCount: params.maxResultCount || 50,
      sorting: params.sorting || null,
      filter: {
        searchText: params.query,
        dateFilter: this.mapDateFilterToApiEnum(params.dateFilter)
      }
    };

    const response = await AppointmentService.postApiAppAppointmentSearch(searchFilter);

    const items = (response.items as Routemize_DTOs_AppointmentListItem[] | undefined) ?? [];
    const kept = items.filter(AppointmentManager.hasLinkedContact);
    const removed = items.length - kept.length;

    return {
      ...response,
      items: kept,
      totalCount: Math.max(0, (response.totalCount ?? items.length) - removed),
    } as Routemize_Core_Dtos_PagedResultDto_1;
  }

  /**
   * Returns true when the appointment still has a linked contact. Appointments
   * whose contact has been deleted have contactId cleared and no contact
   * name left — those are treated as orphaned.
   */
  static hasLinkedContact(apt: Routemize_DTOs_AppointmentListItem): boolean {
    if (apt.contactId) return true;
    const first = apt.contactFirstName?.trim();
    const last = apt.contactLastName?.trim();
    return !!(first || last);
  }

  /**
   * Get appointments grouped by status for Kanban view
   * @param dateFilter - Date filter preset (today, this-week, this-month, etc.)
   * @param teamMemberId - Optional team member filter
   * @returns Appointments grouped by status using API DTOs
   */
  async getAppointmentsByStatus(
    dateFilter: DateFilterPreset = 'today',
    teamMemberId?: string
  ): Promise<Record<string, Routemize_DTOs_AppointmentListItem[]>> {
    // Fetch appointments with the selected date filter
    const searchParams: AppointmentSearchParams = {
      dateFilter,
      teamMemberId,
      maxResultCount: 500
    };
    
    const result = await this.searchAppointments(searchParams);
    const allAppointments = (result.items as Routemize_DTOs_AppointmentListItem[]) || [];

    const results: Record<string, Routemize_DTOs_AppointmentListItem[]> = {
      scheduled: [],
      completed: [],
      cancelled: [],
      noShow: [],
      archived: []
    };

    // Filter appointments into the correct columns based on their actual status
    // Use a Set to track IDs and avoid duplicates
    const seenIds = new Set<string>();

    allAppointments.forEach(appointment => {
      // Skip duplicates
      if (appointment.id && seenIds.has(appointment.id)) {
        return;
      }
      if (appointment.id) {
        seenIds.add(appointment.id);
      }
      
      // The status field comes from the API as the enum value (e.g., "Scheduled", "Completed")
      const status = appointment.status?.toString();

      switch (status) {
        case 'Scheduled':
        case 'scheduled':
        case 'OnTheWay':
        case 'InProgress':
          results.scheduled.push(appointment);
          break;
        case 'Completed':
        case 'completed':
          results.completed.push(appointment);
          break;
        case 'Cancelled':
        case 'cancelled':
          results.cancelled.push(appointment);
          break;
        case 'NoShow':
        case 'No Show':
        case 'no-show':
          results.noShow.push(appointment);
          break;
        case 'Archived':
        case 'archived':
          results.archived.push(appointment);
          break;
        default:
          break;
      }
    });
    
    return results;
  }

  /**
   * Update appointment status with business logic
   * @param appointmentId - Appointment ID
   * @param status - New status from API enum
   * @returns Updated appointment DTO
   */
  async updateAppointmentStatus(
    appointmentId: string, 
    status: Routemize_Constants_AppointmentStatus
  ): Promise<Routemize_DTOs_AppointmentDTO> {
    // Get current appointment details so the update preserves all fields the
    // backend expects on the full DTO (address, times, notification flags…).
    const currentAppointment = await AppointmentService.getApiAppAppointmentGetById(appointmentId);

    const updateDto: Routemize_DTOs_UpdateAppointmentDTO = {
      id: appointmentId,
      status,
      startTimeUtc: currentAppointment.startTimeUtc ?? null,
      endTimeUtc: currentAppointment.endTimeUtc ?? null,
      addressLine1: currentAppointment.addressLine1 ?? null,
      addressLine2: currentAppointment.addressLine2 ?? null,
      city: currentAppointment.city ?? null,
      state: currentAppointment.state ?? null,
      zipCode: currentAppointment.zipCode ?? null,
      latitude: currentAppointment.latitude ?? null,
      longitude: currentAppointment.longitude ?? null,
      notes: currentAppointment.notes ?? null,
      accessCode: currentAppointment.accessCode ?? null,
      sendSmsConfirmation: currentAppointment.sendSmsConfirmation ?? null,
      sendEmailConfirmation: currentAppointment.sendEmailConfirmation ?? null,
      sendSmsReminders: currentAppointment.sendSmsReminders ?? null,
      sendEmailReminders: currentAppointment.sendEmailReminders ?? null,
      contactAddressId: currentAppointment.contactAddressId ?? null,
    };

    return await AppointmentService.putApiAppAppointmentUpdate(updateDto);
  }

  /**
   * Archive an appointment.
   *
   * Archiving is just a status change to `Archived`, but we go through the
   * full-DTO update path (same shape as `rescheduleAppointmentTime`) instead
   * of a `{ id, status }` partial. The backend `UpdateAppointment` endpoint
   * expects the full record, and partial payloads have been observed to
   * silently no-op for the Archived transition — which is the user-visible
   * "archive doesn't work" symptom on the History view.
   *
   * @param appointmentId - Appointment to archive
   * @returns Updated appointment DTO (status === Archived)
   */
  async archiveAppointment(
    appointmentId: string
  ): Promise<Routemize_DTOs_AppointmentDTO> {
    return this.updateAppointmentStatus(
      appointmentId,
      Routemize_Constants_AppointmentStatus.ARCHIVED
    );
  }

  /**
   * Get appointment details for viewing/editing
   * @param appointmentId - Appointment ID
   * @returns Detailed appointment data from API
   */
  async getAppointmentDetail(appointmentId: string): Promise<Routemize_DTOs_AppointmentDetailDTO> {
    return await AppointmentService.getApiAppAppointmentGetAppointmentDetail(appointmentId);
  }

  /**
   * Move an existing appointment to a new time slot without recreating it.
   *
   * The backend `UpdateAppointment` endpoint accepts the full appointment
   * record, so we fetch the current values and only swap the start/end times.
   * Address, contact, notes, and notification flags are preserved as-is so
   * nothing else about the appointment changes.
   *
   * @param appointmentId - Appointment to move
   * @param newStartUtc - New start time (UTC)
   * @param newEndUtc - New end time (UTC)
   * @returns Updated appointment DTO
   */
  async rescheduleAppointmentTime(
    appointmentId: string,
    newStartUtc: Date,
    newEndUtc: Date
  ): Promise<Routemize_DTOs_AppointmentDTO> {
    const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);

    const updateDto: Routemize_DTOs_UpdateAppointmentDTO = {
      id: appointmentId,
      status: current.status ?? Routemize_Constants_AppointmentStatus.SCHEDULED,
      startTimeUtc: newStartUtc.toISOString(),
      endTimeUtc: newEndUtc.toISOString(),
      addressLine1: current.addressLine1 ?? null,
      addressLine2: current.addressLine2 ?? null,
      city: current.city ?? null,
      state: current.state ?? null,
      zipCode: current.zipCode ?? null,
      latitude: current.latitude ?? null,
      longitude: current.longitude ?? null,
      notes: current.notes ?? null,
      accessCode: current.accessCode ?? null,
      sendSmsConfirmation: current.sendSmsConfirmation ?? null,
      sendEmailConfirmation: current.sendEmailConfirmation ?? null,
      sendSmsReminders: current.sendSmsReminders ?? null,
      sendEmailReminders: current.sendEmailReminders ?? null,
      contactAddressId: current.contactAddressId ?? null,
    };

    return await AppointmentService.putApiAppAppointmentUpdate(updateDto);
  }

}